import { Component, EventEmitter, input, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import {
    CompanyView,
    QuickSearchCompaniesParameters,
    SearchAvailableAccountCompaniesParameters,
    SearchAvailableBuildingCompaniesParameters,
    SearchAvailableOpportunityCompaniesParameters
} from '@api-proxies';
import { BuildingService } from '@http/clients/building.service';
import { CompanyService } from '@http/clients/company.service';
import { OpportunityCompanyService } from '@http/clients/opportunity-company.service';
import { ITypeAheadDropdownEntityParameters } from '@interfaces/type-ahead-dropdown-entity-parameters.interface';
import { EntityTypes } from '@models/enums/EntityTypes';
import { GenericTypeAheadDropdownComponent, ITypeAheadDropdownAction, ModalService } from 'ngx-lib';
import { CustomMenuItemComponent } from '../custom-menu-item/custom-menu-item.component';
import { ModalAddCompanyComponent } from '../modals/modal-add-company/modal-add-company.component';

@Component({
    selector: 'app-type-ahead-companies',
    templateUrl: './type-ahead-companies.component.html',
    styleUrls: ['./type-ahead-companies.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [GenericTypeAheadDropdownComponent, CustomMenuItemComponent]
})
export class TypeAheadCompaniesComponent implements OnInit {
    @Input()
    selectedCompany?: CompanyView;

    @Input()
    isReadOnly?: boolean;

    @Input()
    editorInfoAllowAction?: boolean;

    @Input()
    isActive?: boolean;

    @Input()
    displaySelected?: boolean;

    @Input()
    searchParameters!: ITypeAheadDropdownEntityParameters;

    @Output()
    selectedCompanyChange = new EventEmitter<CompanyView>();

    path = input<string | undefined>();

    source?: CompanyView[];

    searchText: string;

    addCompanyAction!: ITypeAheadDropdownAction;

    constructor(
        private readonly companyService: CompanyService,
        private readonly buildingService: BuildingService,
        private readonly modalService: ModalService,
        private readonly opportunityCompanyService: OpportunityCompanyService
    ) {
        this.searchText = '';
        this.displaySelected = false;
    }

    ngOnInit(): void {
        this.addCompanyAction = {
            includeAction: !!this.editorInfoAllowAction,
            name: '+ Add new company',
            action: () => this.openModal()
        };
    }

    async openModal(): Promise<void> {
        this.selectedCompany = await this.modalService.open(ModalAddCompanyComponent);
        if (this.selectedCompany) {
            this.selectedCompanyChange.emit(this.selectedCompany);
        }
    }

    async onCompanyTextChanged(): Promise<void> {
        if (!this.searchText) return;

        this.source = await this.searchByEntity(this.searchParameters?.entityKeyName);
    }

    onCompanyChange(itemSelected: CompanyView): void {
        this.selectedCompany = itemSelected;
        this.selectedCompanyChange.emit(itemSelected);
    }

    async searchByEntity(entityId?: EntityTypes): Promise<CompanyView[]> {
        const searchConfig = {
            filterText: this.searchText,
            ...this.searchParameters?.entity
        };

        this.source = [];

        switch (entityId) {
            case EntityTypes.Building:
                return await this.buildingService.searchAvailableCompanies(
                    searchConfig as SearchAvailableBuildingCompaniesParameters
                );
            case EntityTypes.Account:
                return await this.companyService.searchAvailableCompanies(
                    searchConfig as SearchAvailableAccountCompaniesParameters
                );
            case EntityTypes.Opportunity:
                return await this.opportunityCompanyService.searchAvailable(
                    searchConfig as SearchAvailableOpportunityCompaniesParameters
                );

            default:
                return await this.companyService.search({
                    filterText: this.searchText,
                    isActive: this.isActive
                } as QuickSearchCompaniesParameters);
        }
    }
}
